/**
 * SELF-CONTAINED LEVEL LOADER — the single source of truth for the three shift
 * levels. Each level lives in its own folder as a declarative `level.json`; this
 * module loads them and reconstructs the EXACT runtime objects the game consumes
 * (SourcePack, LandingConfig, subject boxes, crowd chrome, story motif, the
 * fair/manip reference edits, and the per-level graph factory).
 *
 * Everything downstream keeps its stable public API: `content/registry.ts`,
 * `content/landing/index.ts`, `content/landing/subjects.ts`,
 * `content/landing/crowds-presentation.ts`, and `shell/lib/story-motifs.ts` all
 * re-point here, so callers of PACK_REGISTRY / SHIFT_PACK_ORDER /
 * landingConfigForPack / crowdChromeForPack / subjectsForPack / storyMotifFor are
 * unchanged.
 *
 * Only the DATA moved to JSON. The one procedural bit — the graph factory — is a
 * uniform `createEditGraph(pack, seed, ops)` wrapper generated here per level, so
 * no bespoke per-level module is required. Shared catalogs (production audio,
 * look/motion/transition/sfx presets, headline templates) are NOT duplicated into
 * JSON; they are wired in here from their shared modules.
 */
import {
  SOURCE_PACK_SCHEMA_VERSION,
  createEditGraph,
  type AssetManifestEntry,
  type EditGraph,
  type EditOp,
  type HeadlineTemplate,
  type RenderVariant,
  type SourcePack,
} from "@desk/core";
import { PRODUCTION_AUDIO_ASSETS } from "../audio-assets.js";
import {
  sharedCreativeCatalogFields,
  stickersForEarlyBeats,
  type HeadlineFraming,
} from "../creative-catalogs.js";
import {
  EMOTION_AXES,
  type AuthoredStickerZone,
  type CrowdEmotionProfile,
  type EmotionAxis,
  type EmotionVector,
  type LandingConfig,
  type StickerCombo,
  type StickerZone,
} from "../../game/emotion-model.js";
import type { SubjectBox } from "../landing/subjects.js";
import type { CrowdChrome } from "../landing/crowds-presentation.js";
import type { StoryMotif } from "../../shell/lib/story-motifs.js";

import vetDayJson from "./vet-day/level.json";
import dramaticCatJson from "./dramatic-cat/level.json";
import officeSnackBanditJson from "./office-snack-bandit/level.json";

// ── PackId + PackEntry (canonical home; re-exported by content/registry.ts) ────

/**
 * The shipped level ids.
 *
 * This union is the ONE thing a new level still has to touch in code, and it
 * earns its keep: it makes every `PACK_REGISTRY[id]` total, which is what keeps
 * ~140 call sites free of undefined checks. `tests/level-content.test.ts` fails
 * if a `levels/*` folder exists that is not listed here, so the mistake it
 * cannot prevent (drop a folder, forget to wire it) is caught immediately.
 */
export type PackId =
  | "scenario-vet-day"
  | "scenario-dramatic-cat"
  | "scenario-office-snack-bandit";

// ── Per-level authored PRESENTATION content (comments + colleague hints) ───────
//
// These carry NO scoring weight. They are pure level-authored copy the results
// screen and the editor's "How it's landing" panel SELECT from by the current
// crowd reaction / unmet wants. Authoring them here (in level.json) keeps all the
// per-crowd voice in DATA — code only picks which line to show.

/** One authored feed comment for a crowd, with its display tone. */
export interface CrowdCommentLine {
  handle: string;
  body: string;
  tone: "hype" | "curious" | "skeptical" | "neutral";
}

/**
 * Per-crowd comment pool: EXACTLY five lines ordered hate→love
 *   [0] backlash/hostile · [1] cold · [2] lukewarm · [3] warming · [4] hyped
 * The results screen shows the line matching that crowd's ACTUAL reaction.
 */
export type LevelComments = Record<string, CrowdCommentLine[]>;

/**
 * Per-crowd, per-emotion-axis plain-language "how to fill this want" hint. The
 * editor's "Ask a colleague" surfaces the hint(s) for the crowd's currently
 * UNMET green wants. Informed by the sim win-configs; authored, never hardcoded.
 */
export type LevelHints = Record<string, Partial<Record<EmotionAxis, string>>>;

export interface PackEntry {
  id: PackId;
  title: string;
  pack: SourcePack;
  createGraph: (seed: number, ops?: EditOp[]) => EditGraph;
  fairOps: () => EditOp[];
  manipOps: () => EditOp[];
  /**
   * Tone of the story. `"serious"` moralizes the cred/Doubt read; `"fun"` keeps
   * it playful (lower `credibilitySensitivity`, so spin barely dings cred).
   */
  vibe: "fun" | "serious";
  /** Optional visual-style hint for a fun pack's baseline grade. */
  videoStyle?: string;
}

// ── The declarative shape of a level.json ─────────────────────────────────────

interface LevelJson {
  id: PackId;
  slug: string;
  title: string;
  vibe: "fun" | "serious";
  videoStyle?: string;
  pack: {
    id: SourcePack["id"];
    version: SourcePack["version"];
    title: SourcePack["title"];
    contentRating: SourcePack["contentRating"];
    tickRate: SourcePack["tickRate"];
    license: SourcePack["license"];
    canonicalRecord: SourcePack["canonicalRecord"];
    beats: SourcePack["beats"];
    entities: SourcePack["entities"];
    claims: SourcePack["claims"];
    ambiguityNotes: SourcePack["ambiguityNotes"];
    captionVocabulary: SourcePack["captionVocabulary"];
    captionGrammar: SourcePack["captionGrammar"];
    visualAssets: AssetManifestEntry[];
    renderVariants: RenderVariant[];
    castSlots: SourcePack["castSlots"];
    headlineTemplates: HeadlineTemplate[];
    stickerEarlyBeats: string[];
    scoringTags: SourcePack["scoringTags"];
    audience: SourcePack["audience"];
    trends: SourcePack["trends"];
    exportSafety: SourcePack["exportSafety"];
  };
  /** Play order within the shift, 1-based. Ascending; ties break on id. */
  order: number;
  fairOps: EditOp[];
  manipOps: EditOp[];
  landing: {
    crowds: Record<string, CrowdEmotionProfile>;
    crowdOrder: string[];
    servedCrowdId: string;
    baseVector: EmotionVector;
    levers: Omit<LandingConfig["levers"], "stickerZones" | "stickerCombos">;
    /**
     * Per-level, per-BEAT STICKER ZONES (finer than Focus subjects) — see
     * `game/emotion-model.ts`. Authored shape: no resolved `startTick`/
     * `endTick` yet — `buildLanding` attaches those from `zone.beatId`'s pack
     * beat.
     */
    stickerZones: AuthoredStickerZone[];
    /** Per-level HAND-CRAFTED (sticker × zone) combos — the ONLY sticker->emotion lever. */
    stickerCombos: StickerCombo[];
  };
  /**
   * The Caption tool's framing cards. `null` synthesizes them from the pack's
   * headline templates, which is what an un-curated level gets.
   */
  headlineFramings: HeadlineFraming[] | null;
  /** Unlock ids granted when this story is completed (was hardcoded in unlocks.ts). */
  unlocksOnComplete: string[];
  subjects: SubjectBox[];
  crowdChrome: CrowdChrome[];
  motif: Omit<StoryMotif, "id">;
  /** Per-crowd hate→love comment pools (results screen). Optional/back-safe. */
  comments?: LevelComments;
  /** Per-crowd, per-axis colleague hints (editor panel). Optional/back-safe. */
  hints?: LevelHints;
}

/**
 * The shipped levels. Everything ABOUT a level is in its level.json; this list
 * only says which files exist, and PLAY ORDER comes from the authored `order`
 * field rather than the order they happen to be written here.
 */
const LEVEL_FILES: readonly LevelJson[] = [
  dramaticCatJson as unknown as LevelJson,
  officeSnackBanditJson as unknown as LevelJson,
  vetDayJson as unknown as LevelJson,
].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

// ── Builders: JSON → runtime objects ──────────────────────────────────────────

function buildPack(level: LevelJson): SourcePack {
  const p = level.pack;
  return {
    schemaVersion: SOURCE_PACK_SCHEMA_VERSION,
    id: p.id,
    version: p.version,
    title: p.title,
    contentRating: p.contentRating,
    tickRate: p.tickRate,
    license: p.license,
    canonicalRecord: p.canonicalRecord,
    beats: p.beats,
    entities: p.entities,
    claims: p.claims,
    ambiguityNotes: p.ambiguityNotes,
    captionVocabulary: p.captionVocabulary,
    captionGrammar: p.captionGrammar,
    assets: [...PRODUCTION_AUDIO_ASSETS, ...p.visualAssets],
    renderVariants: p.renderVariants,
    castSlots: p.castSlots,
    headlineTemplates: p.headlineTemplates,
    ...sharedCreativeCatalogFields(stickersForEarlyBeats(p.stickerEarlyBeats)),
    scoringTags: p.scoringTags,
    audience: p.audience,
    trends: p.trends,
    exportSafety: p.exportSafety,
  };
}

/**
 * Resolve each authored zone's `beatId` to the pack beat's source-tick range.
 * An authored `beatId` that names no beat in this level's pack is an
 * authoring error — fail loudly rather than silently drop the zone.
 */
function resolveStickerZones(level: LevelJson): StickerZone[] {
  const beats = level.pack.beats;
  return level.landing.stickerZones.map((z) => {
    const beat = beats.find((b) => b.id === z.beatId);
    if (!beat) {
      throw new Error(
        `Level "${level.id}": sticker zone "${z.id}" references unknown beatId "${z.beatId}" ` +
          `(known beats: ${beats.map((b) => b.id).join(", ")})`,
      );
    }
    return { ...z, startTick: beat.startTick, endTick: beat.endTick };
  });
}

function buildLanding(level: LevelJson): LandingConfig {
  const l = level.landing;
  return {
    packId: level.pack.id,
    axes: EMOTION_AXES,
    crowds: l.crowds,
    crowdOrder: l.crowdOrder,
    servedCrowdId: l.servedCrowdId,
    baseVector: l.baseVector,
    levers: { ...l.levers, stickerZones: resolveStickerZones(level), stickerCombos: l.stickerCombos },
  };
}

/** Fresh copies each call, matching the original per-call op literals. */
function cloneOps(ops: EditOp[]): EditOp[] {
  return structuredClone(ops);
}

interface LoadedLevel {
  id: PackId;
  entry: PackEntry;
  pack: SourcePack;
  landing: LandingConfig;
  subjects: SubjectBox[];
  crowdChrome: CrowdChrome[];
  motif: StoryMotif;
  comments: LevelComments;
  hints: LevelHints;
  headlineFramings: HeadlineFraming[] | null;
  unlocksOnComplete: string[];
}

function loadLevel(level: LevelJson): LoadedLevel {
  const pack = buildPack(level);
  const createGraph = (seed: number, ops: EditOp[] = []) => createEditGraph(pack, seed, ops);
  const entry: PackEntry = {
    id: level.id,
    title: level.title,
    pack,
    createGraph,
    fairOps: () => cloneOps(level.fairOps),
    manipOps: () => cloneOps(level.manipOps),
    vibe: level.vibe,
    ...(level.videoStyle ? { videoStyle: level.videoStyle } : {}),
  };
  return {
    id: level.id,
    entry,
    pack,
    landing: buildLanding(level),
    subjects: level.subjects,
    crowdChrome: level.crowdChrome,
    motif: { id: level.id, ...level.motif },
    comments: level.comments ?? {},
    hints: level.hints ?? {},
    headlineFramings: level.headlineFramings ?? null,
    unlocksOnComplete: level.unlocksOnComplete ?? [],
  };
}

const LOADED: readonly LoadedLevel[] = LEVEL_FILES.map(loadLevel);
const BY_ID = new Map<PackId, LoadedLevel>(LOADED.map((l) => [l.id, l]));

function required(id: PackId): LoadedLevel {
  const l = BY_ID.get(id);
  if (!l) throw new Error(`Unknown level id: ${id}`);
  return l;
}

// ── Registry ──────────────────────────────────────────────────────────────────

/**
 * Deterministic shift order: fun-first onboarding — the two playful levels lead
 * (Dramatic Cat → Office Snack Bandit) and the serious Vet Day story lands as the
 * finale.
 */
export const SHIFT_PACK_ORDER: readonly PackId[] = LOADED.map((l) => l.id);

export const PACK_REGISTRY: Readonly<Record<PackId, PackEntry>> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.entry]),
) as Record<PackId, PackEntry>;

// ── Landing configs ────────────────────────────────────────────────────────────

const LANDING_CONFIGS: Record<string, LandingConfig> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.landing]),
);

/**
 * Neutral fallback for an unknown / missing pack id: keeps Vet Day's crowds +
 * lever magnitudes available so the UI renders, but zeroes the raw-footage
 * baseline and drops the clean-end map, matching the engine's original
 * unknown-pack behaviour.
 */
export const FALLBACK_LANDING_CONFIG: LandingConfig = {
  ...required("scenario-vet-day").landing,
  packId: "unknown",
  baseVector: { Rage: 0, Alarm: 0, Doubt: 0, Funny: 0, Pity: 0, Warm: 0 },
  levers: { ...required("scenario-vet-day").landing.levers, beatCleanEnds: {} },
};

/** The landing config for a pack id (or a neutral fallback if unknown). */
export function landingConfigForPack(packId?: string | null): LandingConfig {
  return (packId ? LANDING_CONFIGS[packId] : undefined) ?? FALLBACK_LANDING_CONFIG;
}

// ── Subject boxes ──────────────────────────────────────────────────────────────

const SUBJECTS_BY_PACK: Record<string, SubjectBox[]> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.subjects]),
);

/** Subject boxes for a pack id (empty when the scenario defines none). */
export function subjectsForPack(packId?: string | null): SubjectBox[] {
  return (packId ? SUBJECTS_BY_PACK[packId] : undefined) ?? [];
}

// ── Headline framings (the Caption tool's cards) ──────────────────────────────

const FRAMINGS_BY_PACK: Record<string, HeadlineFraming[] | null> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.headlineFramings]),
);

/**
 * A level's authored Caption framing cards, or null when it has none and the
 * tool should synthesize them from the pack's headline templates.
 */
export function headlineFramingsForPackId(packId?: string | null): HeadlineFraming[] | null {
  return (packId ? FRAMINGS_BY_PACK[packId] : undefined) ?? null;
}

// ── Story progression ─────────────────────────────────────────────────────────

const UNLOCKS_BY_PACK: Record<string, string[]> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.unlocksOnComplete]),
);

/** Unlock ids a level grants on completion (authored per level, never inferred). */
export function unlocksForPackId(packId?: string | null): string[] {
  return (packId ? UNLOCKS_BY_PACK[packId] : undefined) ?? [];
}

// ── Crowd chrome ────────────────────────────────────────────────────────────────

const CROWD_CHROME_BY_PACK: Record<string, CrowdChrome[]> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.crowdChrome]),
);

/** Brief-order crowd chrome for a pack (empty when the pack defines none). */
export function crowdChromeForPack(packId?: string | null): CrowdChrome[] {
  return (packId ? CROWD_CHROME_BY_PACK[packId] : undefined) ?? [];
}

/** Crowd chrome for a pack keyed by crowd id (for quick per-crowd lookup). */
export function crowdChromeMap(packId?: string | null): Record<string, CrowdChrome> {
  const out: Record<string, CrowdChrome> = {};
  for (const c of crowdChromeForPack(packId)) out[c.id] = c;
  return out;
}

// ── Per-crowd comment pools + colleague hints (authored presentation copy) ─────

const COMMENTS_BY_PACK: Record<string, LevelComments> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.comments]),
);

/** Authored hate→love comment pools for a pack, keyed by crowd id (empty if none). */
export function commentsForPack(packId?: string | null): LevelComments {
  return (packId ? COMMENTS_BY_PACK[packId] : undefined) ?? {};
}

const HINTS_BY_PACK: Record<string, LevelHints> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.hints]),
);

/** Authored per-crowd, per-axis colleague hints for a pack (empty if none). */
export function hintsForPack(packId?: string | null): LevelHints {
  return (packId ? HINTS_BY_PACK[packId] : undefined) ?? {};
}

// ── Story motifs ────────────────────────────────────────────────────────────────

export const STORY_MOTIFS: Record<string, StoryMotif> = Object.fromEntries(
  LOADED.map((l) => [l.id, l.motif]),
);

// ── Named per-level exports (stable identities used by tests + shell) ──────────

export const VET_DAY_PACK = required("scenario-vet-day").pack;
export const DRAMATIC_CAT_PACK = required("scenario-dramatic-cat").pack;
export const OFFICE_SNACK_BANDIT_PACK = required("scenario-office-snack-bandit").pack;

export const createVetDayGraph = required("scenario-vet-day").entry.createGraph;
export const createDramaticCatGraph = required("scenario-dramatic-cat").entry.createGraph;
export const createOfficeSnackBanditGraph =
  required("scenario-office-snack-bandit").entry.createGraph;

export const dramaticCatFairOps = required("scenario-dramatic-cat").entry.fairOps;
export const dramaticCatManipOps = required("scenario-dramatic-cat").entry.manipOps;
export const officeSnackBanditFairOps = required("scenario-office-snack-bandit").entry.fairOps;
export const officeSnackBanditManipOps = required("scenario-office-snack-bandit").entry.manipOps;

export const VET_DAY_LANDING_CONFIG = required("scenario-vet-day").landing;
export const DRAMATIC_CAT_LANDING_CONFIG = required("scenario-dramatic-cat").landing;
export const OFFICE_SNACK_BANDIT_LANDING_CONFIG = required("scenario-office-snack-bandit").landing;

function variantIdOf(id: PackId): string {
  return required(id).pack.renderVariants[0]!.id;
}
function videoAssetIdOf(id: PackId): string {
  return required(id).pack.renderVariants[0]!.config.videoAssetId as string;
}

export const VET_DAY_HYBRID_VARIANT_ID = variantIdOf("scenario-vet-day");
export const DRAMATIC_CAT_HYBRID_VARIANT_ID = variantIdOf("scenario-dramatic-cat");
export const OFFICE_SNACK_BANDIT_HYBRID_VARIANT_ID = variantIdOf("scenario-office-snack-bandit");

export const VET_DAY_VIDEO_ASSET_ID = videoAssetIdOf("scenario-vet-day");
export const DRAMATIC_CAT_VIDEO_ASSET_ID = videoAssetIdOf("scenario-dramatic-cat");
export const OFFICE_SNACK_BANDIT_VIDEO_ASSET_ID = videoAssetIdOf("scenario-office-snack-bandit");
